import { useDraggable } from "@dnd-kit/core";
import useLongTap from "hooks/longTap";
import { useResizable } from "hooks/useResizable";
import { getPositionMouseOrTouch } from "modules/device";
import isInputElement from "modules/isInputElement";
import { Resizable } from "re-resizable";
import React, {
  ComponentPropsWithRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
  SyntheticEvent,
  MouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  TouchEvent,
  useLayoutEffect,
} from "react";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "stores";
import { SelectType } from "stores/modules/app.state";
import { appStateMutate } from "stores/modules/app.state/operations";
import { getAppState } from "stores/modules/app.state/selectors";
import { getRoomObjectsTransform } from "stores/modules/entities.room.transform/selector";
import { getCurrentRoom } from "stores/modules/entities.rooms/selectors";
import styled from "styled-components";
import theme from "theme";

type Point = {
  x: number;
  y: number;
};

export type Position = Point & {
  width: number;
  height: number;
};

type EnableProps = {
  bottom?: boolean;
  bottomLeft?: boolean;
  bottomRight?: boolean;
  left?: boolean;
  right?: boolean;
  top?: boolean;
  topLeft?: boolean;
  topRight?: boolean;
};

type DraggableItemProps = {
  style?: React.CSSProperties;
  initialPosition: Position;
  type: SelectType;
  draggableId: string;
  disableDragging: boolean;
  enableResizing: boolean;
  enableProps?: EnableProps;
  lockAspectRatio?: boolean;
  angle?: number;
  locked?: boolean;
  setDragging?: (dragging: boolean) => void;
  onUpdate?: (position: Position) => void;
  onItemKeyDown?: (event: KeyboardEvent) => void;
  onItemContextMenu?: (event: MouseEvent | TouchEvent) => void;
} & ComponentPropsWithRef<"div">;

const DEFAULT_ENABLE_PROPS = {
  bottom: true,
  bottomLeft: true,
  bottomRight: true,
  left: true,
  right: true,
  top: true,
  topLeft: true,
  topRight: true,
};

const DISABLE_RESIZE_PROPS = {
  bottom: false,
  bottomLeft: false,
  bottomRight: false,
  left: false,
  right: false,
  top: false,
  topLeft: false,
  topRight: false,
};

const calcPosition = (
  transform: number | undefined,
  position: number,
  scale: number,
  cellSize: number
) => {
  const calcedTransform = (transform || 0) / scale;
  const calcedPosition = position * cellSize;
  return calcedPosition + calcedTransform;
};

const DraggableItem = ({
  style,
  initialPosition,
  type,
  draggableId,
  disableDragging,
  enableResizing,
  enableProps,
  lockAspectRatio,
  angle,
  locked,
  setDragging,
  onUpdate,
  onItemKeyDown,
  onItemContextMenu,
  children,
  ...props
}: DraggableItemProps) => {
  const dispatch = useAppDispatch();
  const { onClick, onDoubleClick } = props;
  const cellSize = useAppSelector((state) =>
    getAppState(state, "roomScreenCellSize")
  );
  const scale = useAppSelector((state) =>
    getAppState(state, "roomScreenScale")
  );

  const [position, setPosition] = useState<Point>({
    x: initialPosition.x,
    y: initialPosition.y,
  });

  const handleUpdate = useCallback(
    (updatePosition: Position) => {
      if (enableResizing && onUpdate) {
        setPosition({
          x: updatePosition.x,
          y: updatePosition.y,
        });
        onUpdate(updatePosition);
      }
    },
    [onUpdate, enableResizing]
  );
  const isResizing = useRef(false);

  const { size, resizePosition, handleResize, handleResizeStop } = useResizable(
    initialPosition,
    handleUpdate,
    isResizing
  );

  const { isSelected, isOnlySelected, isMultiSelected, notAllSelected } =
    useAppSelector((state) => {
      const selectedObjects = getAppState(state, "selectedObjects");
      const exist = selectedObjects.find((item) => item.id === draggableId);
      const characters = selectedObjects.filter(
        (o) => o.selectType === "character"
      );
      const alignWithGrid = getCurrentRoom(state)?.alignWithGrid ?? true;
      const isSelected = !!exist;

      return {
        isSelected,
        isOnlySelected: isSelected && selectedObjects.length === 1,
        isMultiSelected: selectedObjects.length > 1,
        notAllSelected: selectedObjects.length === 0,
        alignWithGrid:
          characters.length === selectedObjects.length ? false : alignWithGrid,
      };
    }, shallowEqual);

  const isMultiSelectMode = useAppSelector((state) =>
    getAppState(state, "isMultiSelectMode")
  );

  const transform: Point | null = useAppSelector((state) =>
    getRoomObjectsTransform(state, { isSelected })
  );

  const isSelectAreaDragging = transform !== null;

  const { attributes, listeners, setNodeRef } = useDraggable({
    id: draggableId,
    disabled: disableDragging,
  });
  const target = useRef<string | null>(null);

  useEffect(() => {
    if (locked) {
      // 他のユーザーによって配置固定された場合などには選択を強制解除
      dispatch(
        appStateMutate((state) => {
          state.selectedObjects = state.selectedObjects.filter(
            (item) => item.id !== draggableId
          );
        })
      );
    }
  }, [locked, draggableId, dispatch]);

  useEffect(() => {
    if (onItemKeyDown && isOnlySelected) {
      const handleKeyDown = (e: KeyboardEvent) => {
        const isOpenMenu = !!document.querySelector(".MuiMenu-paper");
        const isOpenDialog = !!document.querySelector(".MuiDialog-paper");
        if (isOpenDialog || isOpenMenu || isInputElement(e)) return;
        onItemKeyDown(e);
      };

      window.addEventListener("keydown", handleKeyDown);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [onItemKeyDown, isOnlySelected]);

  useLayoutEffect(() => {
    if (!isSelectAreaDragging) {
      // ドラッグ終了時に反映
      setPosition({
        x: initialPosition.x,
        y: initialPosition.y,
      });
    }
  }, [initialPosition.x, initialPosition.y, isSelectAreaDragging]);

  useEffect(() => {
    if (setDragging) {
      setDragging(isSelectAreaDragging);
    }
    if (isSelectAreaDragging) {
      target.current = null;
    }
  }, [setDragging, isSelectAreaDragging]);

  const positionX = useMemo(
    () =>
      calcPosition(
        transform?.x,
        position.x - resizePosition.x,
        scale,
        cellSize
      ),
    [transform?.x, position.x, resizePosition.x, scale, cellSize]
  );

  const positionY = useMemo(
    () =>
      calcPosition(
        transform?.y,
        position.y - resizePosition.y,
        scale,
        cellSize
      ),
    [transform?.y, position.y, resizePosition.y, scale, cellSize]
  );

  const handlePointerDown = useCallback(
    (e) => {
      if (disableDragging) return;
      e.stopPropagation();
      const selectItem = { selectType: type, id: draggableId };
      if (e.shiftKey) {
        if (!isSelected) {
          dispatch(
            appStateMutate((state) => {
              state.selectedObjects.push(selectItem);
            })
          );
        } else {
          target.current = draggableId;
        }
      } else if (!isSelected) {
        dispatch(
          appStateMutate((state) => {
            state.selectedObjects = [selectItem];
          })
        );
      }
    },
    [dispatch, draggableId, type, isSelected, disableDragging]
  );

  const handlePointerUp = useCallback(
    (e) => {
      // onResizeStopの後にonClickが発火しているためこのタイミングでリサイズを終了
      isResizing.current = false;
      if (e.shiftKey && target.current) {
        dispatch(
          appStateMutate((state) => {
            state.selectedObjects = state.selectedObjects.filter(
              (item) => item.id !== target.current
            );
          })
        );
      }
      target.current = null;
    },
    [dispatch]
  );

  const handleClick = useCallback(
    (e) => {
      if (!isMultiSelected && onClick && !isResizing.current && !e.shiftKey) {
        onClick(e);
      }
    },
    [onClick, isMultiSelected]
  );

  const handleContextMenu = useCallback(
    (e: TouchEvent | MouseEvent) => {
      if (isSelected && !isOnlySelected) {
        e.preventDefault();
        e.stopPropagation();

        const position = getPositionMouseOrTouch(e);
        dispatch(
          appStateMutate((state) => {
            state.openRoomSelectedObjectsMenu = true;
            state.roomPointerY = position.y;
            state.roomPointerX = position.x;
          })
        );
      } else {
        if (onItemContextMenu) onItemContextMenu(e);
      }
    },
    [onItemContextMenu, isSelected, isOnlySelected, dispatch]
  );

  const longTapProps = useLongTap(handleContextMenu);

  const handleDraggableItemKeydown = useCallback(
    (e: ReactKeyboardEvent) => {
      // いずれのオブジェクトも選択されていない場合のみフォーカスされているアイテムのkeydownイベントを使用
      if (onItemKeyDown && notAllSelected) onItemKeyDown(e.nativeEvent);
    },
    [onItemKeyDown, notAllSelected]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (!isMultiSelected && onDoubleClick && !e.shiftKey) {
        onDoubleClick(e);
      }
    },
    [onDoubleClick, isMultiSelected]
  );

  return (
    <DraggableContent
      data-field-object={draggableId}
      className={isSelectAreaDragging ? "" : "movable"}
      ref={setNodeRef}
      style={{
        ...style,
        cursor: isMultiSelectMode
          ? "default"
          : disableDragging
          ? "auto"
          : "move",
        transform: `translate(${positionX}px, ${positionY}px)`,
      }}
      isSelected={isSelected}
      {...props}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onContextMenu={handleContextMenu}
      onKeyDown={handleDraggableItemKeydown}
      onDoubleClick={handleDoubleClick}
    >
      <Resizable
        className={"movable"}
        size={size}
        scale={scale}
        minHeight={cellSize}
        minWidth={cellSize}
        enable={
          isMultiSelected
            ? DISABLE_RESIZE_PROPS
            : enableResizing
            ? enableProps
              ? enableProps
              : DEFAULT_ENABLE_PROPS
            : {}
        }
        grid={[cellSize, cellSize]}
        onResizeStart={preventDefault}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        lockAspectRatio={lockAspectRatio}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
          }}
          {...attributes}
          {...listeners}
          {...longTapProps}
        >
          {children}
        </div>
      </Resizable>
    </DraggableContent>
  );
};

const preventDefault = (e: SyntheticEvent) => {
  e.preventDefault();
};

const DraggableContent = styled.div<{ isSelected: boolean }>`
  position: absolute;
  user-select: auto;
  touch-action: none;
  display: inline-block;
  top: 0;
  left: 0;
  box-sizing: border-box;
  > .movable img {
    ${({ isSelected }) =>
      isSelected && `outline: 2px solid ${theme.palette.primary.main};`}
  }
`;

export default memo(DraggableItem);
